import ApiService, { ApiResponse } from './ApiService';
import { PAGINATION_CONSTANTS } from '../constants/pagination';

export interface Submission {
  id: string;
  submission_id: string;
  submission_date?: string;
  client_id?: string;
  client_name?: string;
  crd_title?: string;
  protocol_id?: number;
  protocol_name?: string;
  job_title?: string;
  instructions?: string;
  dicom_file_count?: number;
  invalid_file_count?: number;
  uploaded_files?: number;
  parameters_to_measure?: string[];
  submission_status: 'pending' | 'in-progress' | 'completed' | 'urgent';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assigned_graders?: string[];
  assignee_ids?: number[];
  created_date?: string;
  due_date?: string;
  created_by?: string;
  ftp_upload_status?: 'uploaded' | 'pending' | 'failed';
  progress?: {
    completed: number;
    total: number;
  };
  // Additional fields that might come from API
  created_at?: string;
  updated_at?: string;
  [key: string]: any; // Allow for additional dynamic fields from API
}

export interface SubmissionListResponse {
  submissions: Submission[];
  total?: number; // Keep for backward compatibility
  total_count: number;
  page: number;
  page_size?: number;
  size: number;
  total_pages: number;
}

export interface GetSubmissionsParams {
  submission_id?: string;
  status?: string;
  search?: string;
  page?: number;
  size?: number;
}

export interface InvalidDicomFile {
  id?: string | number;
  submissionId?: number;
  jobId?: number;
  fileName: string;
  errorCode?: string;
  errorMessage?: string;
  sourcePath?: string;
  quarantinePath?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  /** @deprecated Use errorMessage — kept for backward compatibility */
  reason?: string;
  patientId?: string;
  studyInstanceUid?: string;
  uploadedAt?: string;
}

export interface InvalidDicomFilesListResponse {
  invalidFiles: InvalidDicomFile[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface GetInvalidDicomFilesParams {
  submission_id: string;
  page?: number;
  size?: number;
  is_active?: boolean;
  search?: string;
}

const extractInvalidFilesListPayload = (data: unknown): Record<string, unknown> => {
  if (!data) {
    return {};
  }

  if (Array.isArray(data)) {
    return (data[0] as Record<string, unknown>) ?? {};
  }

  if (typeof data === 'object') {
    const envelope = data as Record<string, unknown>;
    const nested = envelope.data;

    if (Array.isArray(nested)) {
      return (nested[0] as Record<string, unknown>) ?? {};
    }

    if (nested && typeof nested === 'object') {
      return nested as Record<string, unknown>;
    }

    return envelope;
  }

  return {};
};

const extractInvalidFilesFromPayload = (listData: Record<string, unknown>): Record<string, unknown>[] => {
  const candidates = [
    listData.quarantine_files,
    listData.invalid_files,
    listData.invalid_dicom_files,
    listData.files,
    listData.items,
    listData.results,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as Record<string, unknown>[];
    }
  }

  return [];
};

const normalizeInvalidDicomFile = (raw: Record<string, unknown>): InvalidDicomFile => {
  const errorMessage = (raw.error_message ??
    raw.reason ??
    raw.validation_error ??
    raw.error ??
    raw.message) as string | undefined;

  return {
    id: (raw.id ?? raw.file_id) as string | number | undefined,
    submissionId: raw.submission_id as number | undefined,
    jobId: raw.job_id as number | undefined,
    fileName:
      (raw.file_name ??
        raw.filename ??
        raw.original_file_name ??
        raw.name ??
        'Unknown file') as string,
    errorCode: (raw.error_code ?? raw.errorCode) as string | undefined,
    errorMessage,
    sourcePath: (raw.source_path ?? raw.sourcePath) as string | undefined,
    quarantinePath: (raw.quarantine_path ?? raw.quarantinePath) as string | undefined,
    isActive: raw.is_active as boolean | undefined,
    createdAt: (raw.created_at ?? raw.uploaded_at ?? raw.uploadedAt) as string | undefined,
    updatedAt: (raw.updated_at ?? raw.updatedAt) as string | undefined,
    reason: errorMessage,
    patientId: (raw.patient_id ?? raw.patient_anon_id ?? raw.patientId) as string | undefined,
    studyInstanceUid: (raw.study_instance_uid ??
      raw.study_uid ??
      raw.studyInstanceUid) as string | undefined,
    uploadedAt: (raw.uploaded_at ?? raw.created_at ?? raw.uploadedAt) as string | undefined,
  };
};

class SubmissionService {
  private static instance: SubmissionService;
  private apiService: ApiService;

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  public static getInstance(): SubmissionService {
    if (!SubmissionService.instance) {
      SubmissionService.instance = new SubmissionService();
    }
    return SubmissionService.instance;
  }

  public async getSubmissions(
    params?: GetSubmissionsParams
  ): Promise<ApiResponse<SubmissionListResponse>> {
    try {
      const queryParams: Record<string, any> = {
        submission_id: params?.submission_id || 'all',
        status: params?.status || 'all',
        page: params?.page || 1,
        size: params?.size || PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
      };

      // Only include search parameter if it's provided and not empty
      const searchValue = params?.search?.toString().trim();
      if (searchValue) {
        queryParams.search = searchValue;
      }

      const response = await this.apiService.get<{
        is_success: boolean;
        status: number;
        message: string;
        data: [{
          submissions: Submission[];
          total_count: number;
          page: number;
          page_size: number;
          total_pages: number;
          total?: number; // Keep for backward compatibility
          size?: number; // Keep for backward compatibility
        }];
      }>('/submissions', queryParams, this.apiService.getWorkOrderServiceUrl());

      if (response.success && response.data && response.data.is_success) {
        const submissionData = response.data.data[0];
        const totalCount = submissionData.total_count ?? submissionData.total ?? 0;
        const pageSize = submissionData.page_size ?? submissionData.size ?? queryParams.size;
        const currentPage = submissionData.page ?? queryParams.page;
        const totalPages = submissionData.total_pages ?? Math.max(1, Math.ceil(totalCount / pageSize));

        const submissionListResponse: SubmissionListResponse = {
          submissions: submissionData.submissions || [],
          total_count: totalCount,
          total: totalCount, // Keep for backward compatibility
          page: currentPage,
          page_size: pageSize,
          size: pageSize,
          total_pages: totalPages,
        };

        return {
          success: true,
          data: submissionListResponse,
        };
      }

      return {
        success: false,
        error: response.data?.message || response.error || 'Failed to fetch submissions',
      };
    } catch (error) {
      console.error('Error fetching submissions:', error);
      return {
        success: false,
        error: 'Network error occurred while fetching submissions',
      };
    }
  }

  public async getSubmissionById(submissionId: string): Promise<ApiResponse<Submission>> {
    try {
      const response = await this.apiService.get<{
        is_success: boolean;
        status: number;
        message: string;
        data: Submission;
      }>(
        `/submissions/${submissionId}`,
        undefined,
        this.apiService.getWorkOrderServiceUrl()
      );

      if (response.success && response.data && response.data.is_success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      return {
        success: false,
        error: response.data?.message || response.error || 'Failed to fetch submission',
      };
    } catch (error) {
      console.error('Error fetching submission:', error);
      return {
        success: false,
        error: 'Network error occurred while fetching submission',
      };
    }
  }

  public async updateSubmissionStatus(
    submissionId: string,
    status: string
  ): Promise<ApiResponse<Submission>> {
    try {
      const response = await this.apiService.put<{
        is_success: boolean;
        status: number;
        message: string;
        data: Submission;
      }>(
        `/submissions/${submissionId}/status`,
        { submission_status: status },
        this.apiService.getWorkOrderServiceUrl()
      );

      if (response.success && response.data && response.data.is_success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      return {
        success: false,
        error: response.data?.message || response.error || 'Failed to update submission status',
      };
    } catch (error) {
      console.error('Error updating submission status:', error);
      return {
        success: false,
        error: 'Network error occurred while updating submission status',
      };
    }
  }

  public async getInvalidDicomFiles(
    params: GetInvalidDicomFilesParams
  ): Promise<ApiResponse<InvalidDicomFilesListResponse>> {
    try {
      const page = params.page || 1;
      const size = params.size || 5;
      const queryParams: Record<string, string | number | boolean> = {
        submission_id: params.submission_id,
        is_active: params.is_active ?? true,
        page,
        size,
      };

      if (params.search?.trim()) {
        queryParams.search = params.search.trim();
      }

      const response = await this.apiService.get<{
        is_success: boolean;
        status?: number;
        message?: string;
        data?: unknown;
        quarantine_files?: Record<string, unknown>[];
        total_count?: number;
        page?: number;
        page_size?: number;
        total_pages?: number;
      }>('/submissions/invalid-files', queryParams, this.apiService.getWorkOrderServiceUrl());

      if (response.success && response.data?.is_success) {
        const listData = extractInvalidFilesListPayload(response.data);
        const rawFiles = extractInvalidFilesFromPayload(listData);
        const totalCount = Number(listData.total_count ?? listData.total ?? 0);
        const pageSize = Number(listData.page_size ?? listData.size ?? size);
        const currentPage = Number(listData.page ?? page);
        const totalPages = Number(
          listData.total_pages ?? Math.max(1, Math.ceil(totalCount / pageSize))
        );

        return {
          success: true,
          data: {
            invalidFiles: rawFiles.map(file => normalizeInvalidDicomFile(file)),
            total_count: totalCount,
            page: currentPage,
            page_size: pageSize,
            total_pages: totalPages,
          },
        };
      }

      return {
        success: false,
        error:
          response.data?.message ||
          response.error ||
          'Failed to fetch invalid files',
      };
    } catch (error) {
      console.error('Error fetching invalid files:', error);
      return {
        success: false,
        error: 'Network error occurred while fetching invalid files',
      };
    }
  }

  public async assignGradersToSubmission(
    submissionId: string,
    graderIds: number[]
  ): Promise<ApiResponse<Submission>> {
    try {
      const response = await this.apiService.put<{
        is_success: boolean;
        status: number;
        message: string;
        data: Submission;
      }>(
        `/submissions/${submissionId}/assign-graders`,
        { grader_ids: graderIds },
        this.apiService.getWorkOrderServiceUrl()
      );

      if (response.success && response.data && response.data.is_success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      return {
        success: false,
        error: response.data?.message || response.error || 'Failed to assign graders',
      };
    } catch (error) {
      console.error('Error assigning graders:', error);
      return {
        success: false,
        error: 'Network error occurred while assigning graders',
      };
    }
  }
}

export default SubmissionService;
